import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { sendOtpEmail, registerConsumer } from "@/api";
import { handleIncompleteRegistration } from "@/lib/handleIncompleteRegistration";
import StepHeader from "@/components/StepHeader";
import ErrorModal from "@/components/ErrorModal";
import { extractApiError } from "@/lib/extractApiError";

const Register = () => {
  sessionStorage.removeItem("migration");
  const navigate = useNavigate();
  const { campaign, referral } = useParams();

  // Step state: "email" or "otp"
  const [view, setView] = useState("email");

  // Email step state
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [expanded, setExpanded] = useState(true);

  // OTP step state
  const [otpCode, setOtpCode] = useState("");
  const [timer, setTimer] = useState(180);
  const [canResend, setCanResend] = useState(false);

  // Shared state
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Save campaign to sessionStorage on mount
  useEffect(() => {
    if (campaign) sessionStorage.setItem("ls", campaign);
  }, [campaign]);

  // OTP countdown timer
  useEffect(() => {
    if (view !== "otp") return;
    if (timer <= 0) {
      setCanResend(true);
      return;
    }
    const id = setTimeout(() => setTimer((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [timer, view]);

  const formatTime = (s) =>
    `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  const isEmailValid = email.includes("@");

  // Step 1a: Send OTP email
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!isEmailValid) return;
    setError(null);
    setIsLoading(true);
    try {
      await sendOtpEmail({ consumer: { emailAddress: email } });
      setView("otp");
      setTimer(180);
      setCanResend(false);
    } catch (err) {
      const errorCode = err.response?.data?.data?.error;
      if (
        errorCode === "error_otp_cooldown" ||
        errorCode === "error_otp_please_wait"
      ) {
        // Code was already sent or is on its way — still transition to OTP view
        setView("otp");
        setTimer(180);
        setCanResend(false);
        if (errorCode === "error_otp_cooldown") {
          setError({ message: "Code wurde bereits gesendet.", errorCode: null });
        }
      } else {
        setError(extractApiError(err.response?.data));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResend = async () => {
    try {
      await sendOtpEmail({ consumer: { emailAddress: email } });
      setTimer(180);
      setCanResend(false);
    } catch {
      /* silent */
    }
  };

  // Step 1b: Verify OTP + Register
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otpCode) return;
    setError(null);
    setIsLoading(true);
    try {
      const payload = {
        consumer: {
          emailAddress: email,
          otpEmail: otpCode,
          marketingConsent: consent,
        },
      };
      if (campaign) payload.campaign = { ident: campaign };
      if (referral) payload.referral = { code: referral };

      await registerConsumer(payload);
      sessionStorage.removeItem("registerData");
    } catch (err) {
      const apiError = err.response?.data;
      if (apiError?.data?.error === "error_registration_incomplete") {
        handleIncompleteRegistration(apiError.data.details, navigate);
      } else {
        setError(extractApiError(apiError));
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Email view (Step 1a)
  if (view === "email") {
    return (
      <>
        <Button
          type="button"
          size="sm"
          className="absolute top-3 left-3 z-10 bg-black text-white hover:bg-black/90 md:h-[54px] md:px-[20px] md:text-[24px] md:rounded-[14px]"
          onClick={() => navigate("/")}
        >
          Anmelden
        </Button>

        <form
          onSubmit={handleSendOtp}
          className="flex flex-col gap-10 items-center w-full max-w-[720px] pt-[80px] pb-[48px] px-5"
        >
          <StepHeader stepTitle="Gib deine E-Mail-Adresse ein" step={1} />

          <div className="flex flex-col gap-6 items-center w-full max-w-[480px]">
            <Input
              type="email"
              label="E-Mail"
              placeholder="E-Mail-Adresse eingeben"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            {/* Consent box */}
            <div className="flex flex-col gap-3 w-full">
              <div className="border border-black/20 rounded-xl p-4">
                <div className={`flex gap-4 w-full ${expanded ? "items-start" : "items-center"}`}>
                  <button
                    type="button"
                    onClick={() => setConsent(!consent)}
                    className={`relative shrink-0 w-[28px] h-[16px] rounded-full transition-colors ${consent ? "bg-primary" : "bg-[#7f7f7f]"}`}
                  >
                    <span
                      className={`absolute top-[2px] w-[12px] h-[12px] rounded-full bg-white transition-transform ${consent ? "left-[14px]" : "left-[2px]"}`}
                    />
                  </button>
                  {expanded ? (
                    <div className="flex flex-col gap-4 items-end flex-1 min-w-0">
                      <p className="text-[10px] leading-[14px] text-black">
                        Ja, ich möchte Warenproben, Informationen und Angebote
                        (individualisierte Angebote und Kundenumfragen) von der
                        British American Tobacco (Germany) GmbH (BAT), Alsterufer 4,
                        20354 Hamburg an meine oben genannte Anschrift erhalten.
                        Zudem möchte ich Newsletter, individualisierte Angebote und
                        Kundenumfragen per E-Mail, SMS und Telefon von BAT erhalten.
                        Ich bin damit einverstanden, dass BAT zu diesen Zwecken meine
                        persönlichen Produkt-Präferenzen auf Basis der von BAT über
                        mich verarbeiteten Daten in einem individuellen Profil
                        zusammenführt.{" "}
                        <a
                          href="/legal/datenschutz"
                          className="underline font-bold"
                        >
                          Datenschutz - Wie wir deine Daten schützen | Lucky Strike
                        </a>
                      </p>
                      <button
                        type="button"
                        onClick={() => setExpanded(false)}
                        className="flex items-center gap-1.5 text-[10px] font-bold text-black uppercase cursor-pointer"
                      >
                        Weniger sehen
                        <ChevronUp className="w-4 h-4 pb-1" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <p className="text-[10px] leading-[14px] text-black flex-1 min-w-0 truncate">
                        Ja, ich möchte Warenproben, Informationen und Angebote
                        (individualisierte Angebote und Kundenumfragen) von der
                        British American Tobacco (Germany) GmbH (BAT), Alsterufer 4,
                        20354 Hamburg an meine oben genannte Anschrift erhalten.
                      </p>
                      <button
                        type="button"
                        onClick={() => setExpanded(true)}
                        className="shrink-0 flex items-center gap-1.5 text-[10px] font-bold text-black uppercase whitespace-nowrap cursor-pointer"
                      >
                        Mehr sehen
                        <ChevronDown className="w-4 h-4 pb-1" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className="border border-[#ababab] rounded-xl p-4">
                <p className="text-[12px] leading-[16px] text-black font-medium">
                  Die obige Einwilligung betrifft folgende Marken: Pall
                  Mall, Lucky Strike, Fair Play, glo™, Vuse.
                </p>
              </div>
              <div className="border border-[#ababab] rounded-xl p-4">
                <p className="text-[12px] leading-[16px] text-black font-medium">
                  Hinweis: Du kannst Deine Einwilligung jederzeit mit
                  Wirkung für die Zukunft widerrufen. Nähere Informationen
                  findest du in unseren{" "}
                  <a href="/legal/datenschutz" className="underline">
                    Datenschutzbestimmungen.
                  </a>
                  <br />
                  Es gelten unsere{" "}
                  <a href="/legal/nutzungsbedingungen" className="underline">
                    Teilnahmebedingungen
                  </a>
                  .
                </p>
              </div>
            </div>

            <ErrorModal
              open={!!error}
              onClose={() => setError(null)}
              message={error?.message}
              errorCode={error?.errorCode}
            />

            <Button
              type="submit"
              className="w-full md:h-[54px] md:text-[24px] md:rounded-[14px]"
              size="sm"
              variant={isEmailValid ? "default" : "disabled"}
              disabled={!isEmailValid || isLoading}
            >
              {isLoading ? "Bitte warte..." : "Weiter"}
            </Button>
          </div>
        </form>
      </>
    );
  }

  // OTP view (Step 1b)
  return (
    <>
      <Button
        type="button"
        size="sm"
        className="absolute top-3 left-3 z-10 bg-black text-white hover:bg-black/90 md:h-[54px] md:px-[20px] md:text-[24px] md:rounded-[14px]"
        onClick={() => navigate("/")}
      >
        Anmelden
      </Button>

      <form
        onSubmit={handleVerifyOtp}
        className="flex flex-col gap-10 items-center w-full max-w-[720px] pt-[80px] pb-[48px] px-5"
      >
        <div className="flex flex-col gap-4 items-center w-full">
          <StepHeader stepTitle="E-Mail-Verifizierung" step={1} migration={sessionStorage.getItem("migration") === "true"} />
          <p className="text-black text-center text-[18px] font-medium leading-[1.4]">
            Wir haben einen Code an{" "}
            <span className="font-extrabold">{email}</span> gesendet. Gib ihn
            unten ein.
          </p>
        </div>

        <div className="flex flex-col gap-6 items-center w-full max-w-[480px]">
          <Input
            label="E-Mail-Code"
            placeholder="Code eingeben"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value)}
          />

          <div className="flex items-center gap-2.5 w-full">
            <div className="flex-1 text-[16px] text-black font-medium">
              <p className="leading-[1.4]">Hast du keinen Code?</p>
              {!canResend && (
                <p className="leading-[1.4]">
                  Bitte warte:{" "}
                  <span className="font-extrabold text-primary">
                    {formatTime(timer)}
                  </span>
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={handleResend}
              disabled={!canResend}
              className={`font-extrabold text-[18px] text-black uppercase text-right leading-[28px] min-h-[40px] ${!canResend ? "opacity-35 pointer-events-none" : "hover:underline cursor-pointer"}`}
            >
              Code erneut senden
            </button>
          </div>

          <ErrorModal
            open={!!error}
            onClose={() => setError(null)}
            message={error?.message}
            errorCode={error?.errorCode}
          />

          <div className="flex gap-2.5 items-start w-full">
            <Button
              type="button"
              className="flex-1 bg-black text-white hover:bg-black/90 md:h-[54px] md:text-[24px] md:rounded-[14px]"
              size="sm"
              onClick={() => setView("email")}
            >
              Zurück
            </Button>

            <Button
              type="submit"
              className="flex-1 md:h-[54px] md:text-[24px] md:rounded-[14px]"
              size="sm"
              variant={!otpCode ? "disabled" : "default"}
              disabled={!otpCode || isLoading}
            >
              {isLoading ? "Bitte warte..." : "Weiter"}
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};

export default Register;
